import { AmountInCents } from "@buildwithflux/models";
import { silentLogger } from "@buildwithflux/shared";
import SparkMD5 from "spark-md5";
import { z } from "zod";
/**
 * Subscriptions can only have a single billing interval, and this means we need separate subscriptions for
 * account subscriptions (which could be annual) and copilot credits (which are always on a monthly period)
 *
 * Implementation detail:
 * - Account subscriptions are stored in the traditional place in the private metadata: the metadata?.payment?.subscriptions
 *   array
 * - Copilot subscriptions are stored in the metadata?.payment?.copilotSubscriptions array
 *
 * Stripe Metadata distinguishes the two, so that we can tell which one a webhook is talking about. But note that these
 * subscriptions could have different active status, different payment details, etc.
 */
export function getPaymentSubscriptions(accountPrivateMetadata, subtype) {
    return ((!subtype || subtype === "account"
        ? accountPrivateMetadata?.payment?.subscriptions
        : accountPrivateMetadata?.payment?.copilotCreditSubscriptions) ?? {});
}
const centsPerDollar = 100;
export function centsToDisplay(cents, fractionDigits = 2, showSymbol = false, locale = undefined, logger = silentLogger) {
    let formatOptions = {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    };
    if (showSymbol) {
        formatOptions = {
            ...formatOptions,
            style: "currency",
            currency: "USD",
            currencyDisplay: "narrowSymbol",
        };
    }
    try {
        const usd = new Intl.NumberFormat(locale, formatOptions);
        const amount = AmountInCents.safeParse(cents);
        if (!amount.success) {
            // Usually indicates NaN, infinity, negative amount, etc.
            return usd.format(0);
        }
        // Get rid of negative zero
        if (Object.is(amount.data, -0)) {
            amount.data = AmountInCents.parse(0);
        }
        return usd.format(amount.data / centsPerDollar);
    }
    catch (error) {
        logger.error(error);
        // Usually indicates Intl.NumberFormat problem
        return showSymbol ? "$0.00" : "0.00";
    }
}
export function displayToCents(monetaryDisplayValue, locale = undefined) {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const thousandSeparator = Intl.NumberFormat(locale).formatToParts(11111)?.[1]?.value ?? ",";
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const decimalSeparator = Intl.NumberFormat(locale).formatToParts(1.1)?.[1]?.value ?? ".";
    return z.coerce
        .number()
        .nonnegative()
        .finite()
        .transform((value) => Math.round(value * centsPerDollar))
        .safeParse(monetaryDisplayValue
        .replace(/[\s$]+/, "")
        .replace(new RegExp("\\" + thousandSeparator, "g"), "")
        .replace(new RegExp("\\" + decimalSeparator), "."));
}
export function displayToAmountInCents(monetaryDisplayValue, amountType, locale = undefined) {
    const cents = displayToCents(monetaryDisplayValue, locale);
    if (!cents.success) {
        return cents;
    }
    return amountType.safeParse(cents.data);
}
/**
 * Gets a stripe verification value for a given account UID
 *
 * We don't use this for anything super-important: the source of truth for what Stripe plans a user has is synced from
 * webhooks into a place in the private metadata the user can't write to. What we do use this for is to prevent the
 * user from _thinking_ that they can bypass the Stripe checkout by changing URL parameters (which they can't; they
 * end up with no plan at all, with locked down entitlements - but it appears as if they can, which is confusing for
 * them).
 *
 * @param accountUid Either the user uid or the organization uid
 *
 * @todo Would have been nice to use something other than MD5 here, but we need it to be isomorphic, and Node doesn't
 *       carry a webcrypto implementation until Node 20+ (which we are probably using by the second half of 2025, but
 *       is bad timing right now)
 */
export function getStripeVerificationValue(accountUid) {
    return SparkMD5.hash(`${accountUid}-we-don't-rely-on-this-for-security-it's-just-to-stop-users-confusing-themselves`).substring(0, 8);
}
/**
 * Checks whether a Stripe verification value is correct for a given account UID
 *
 * We extract this to a function so that if we need to change hash formats in the future, we can just change this one
 * check
 */
export function isStripeVerificationValueCorrect(accountUid, verificationValue) {
    return verificationValue === getStripeVerificationValue(accountUid);
}
export function centsToCompactDisplay(cents, locale, mode = "currency") {
    const value = Number(cents) / 100;
    const formatCurrencySmartDecimals = (totalCents) => {
        const amount = Number(totalCents) / 100;
        const hasWholeDollars = Number(totalCents) % 100 === 0;
        return amount.toLocaleString(locale || undefined, {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: hasWholeDollars ? 0 : 2,
            maximumFractionDigits: hasWholeDollars ? 0 : 2,
        });
    };
    if (mode === "cents") {
        const centsValue = Math.round(Number(cents));
        return `${centsValue}¢`;
    }
    if (mode === "currency") {
        return formatCurrencySmartDecimals(Number(cents));
    }
    if (value > 0 && value < 1) {
        const centsValue = Math.round(Number(cents));
        return `${centsValue}¢`;
    }
    return formatCurrencySmartDecimals(Number(cents));
}
//# sourceMappingURL=helpers.js.map